import { scopeProjectRef, scopeThreadRef, scopedThreadKey } from "@cmmd-forge/client-runtime";
import {
  type BrowserCommand,
  type BrowserViewport,
  type DevTunnelRecord,
  type BrowserSessionSnapshot,
  type BrowserTabSnapshot,
  type EnvironmentId,
  type ProjectId,
  type ThreadId,
} from "@cmmd-forge/contracts";
import {
  formatBrowserErrorDescription,
  normalizeBrowserError,
} from "@cmmd-forge/shared/browserErrors";
import { browserClientCapability } from "./BrowserPreviewPanel.clientCapability";
import { ActivityIcon } from "lucide-react";
import { type FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ensureEnvironmentApi, useEnvironmentApi } from "~/environmentApi";
import { readLocalApi } from "~/localApi";
import { randomUUID } from "~/lib/utils";
import { selectBrowserThreadState, useBrowserStateStore } from "~/browserStateStore";
import { useSettings, useUpdateSettings } from "~/hooks/useSettings";
import { useStore } from "~/store";
import { createProjectSelectorByRef, createThreadSelectorByRef } from "~/storeSelectors";
import {
  selectThreadTerminalPreviewUrl,
  selectThreadTerminalState,
  useTerminalStateStore,
} from "~/terminalStateStore";
import { useRightPanelStore } from "~/rightPanelStore";
import { clearAllTrackedRpcRequests } from "~/rpc/requestLatencyState";
import { toastManager } from "./ui/toast";
import { useComposerDraftStore } from "~/composerDraftStore";
import { useCommandPaletteStore } from "~/commandPaletteStore";
import {
  type BrowserAddressCommand,
  type BrowserHistoryRestoreDecision,
  browserDisplayUrl,
  browserSessionHasAddressUrl,
  buildBrowserOptionsMenuItems,
  deriveBrowserHistoryRestoreDecision,
  deriveBrowserPreviewPanelSessionState,
  deriveRestorableThreadBrowserUrl,
  shouldActivateBrowserTabFromRoute,
  shouldSyncPreviewTabIdToUrl,
  isLocalPreviewBrowserUrl,
  isRecoverableBrowserSessionCommandError,
  resolveBrowserConsoleSummary,
  resolveBrowserCommandRuntimeAvailability,
  resolveBrowserDevServerLaunchCardVm,
  resolveBrowserAccessTarget,
  deriveBrowserPreviewPanelInteractionState,
  deriveBrowserPreviewPanelRuntimeState,
  normalizeBrowserDisplayForPreview,
  resolveDevServerLaunchPlan,
  shouldRestartLocalPreviewAddressCommand,
  resolveDevServerRelaunchTargetUrl,
  shouldShowDevServerLaunchCard,
} from "./BrowserPreviewPanel.logic";
import { useBrowserAddressCommands } from "./BrowserPreviewPanel.addressCommands";
import { useBrowserSessionControls } from "./BrowserPreviewPanel.sessionControls";
import {
  selectBrowserCommentDraft,
  useBrowserCommentDraftStore,
} from "./browser/browserCommentDraftStore";
import {
  BrowserDevServerLaunchCard,
  latestThreadDevServerPreviewUrlFromSources,
  useBrowserDevServerLauncher,
} from "./BrowserPreviewPanel.devServerLauncher";
import {
  type BrowserPreviewLifecycleEffectsOptions,
  useBrowserPreviewLifecycleEffects,
} from "./BrowserPreviewPanel.lifecycleEffects";
import { useBrowserLauncher, useRecordBrowserVisit } from "../browser/launcher/useBrowserLauncher";
import { shouldShowBrowserLauncher } from "../browser/launcher/browserLauncherLogic";
import { useBrowserOpenSession } from "./BrowserPreviewPanel.openSession";
import {
  type BrowserCommandRpcResult,
  resolveBrowserCommandErrorDecision,
  resolveServerBrowserCommandResult,
} from "./BrowserPreviewPanel.commandResult";
import {
  type BrowserCommandRunOptions,
  useBrowserRunCommand,
} from "./BrowserPreviewPanel.runCommand";
import { useBrowserCommentCapture } from "./BrowserPreviewPanel.commentCapture";
import { useBrowserDisplayLifecycle } from "./BrowserPreviewPanel.displayLifecycle";
import { useBrowserViewportSync } from "./BrowserPreviewPanel.viewportSync";
import { BrowserChrome } from "~/browser/shell/BrowserChrome";
import { BrowserSurfaceHost } from "~/browser/shell/BrowserSurfaceHost";

// Browser tabs should start at a controlled blank page. Public search engines
// commonly challenge automated/container browsers, so dev-server previews remain
// an explicit workflow instead of becoming the Browser startup URL.
const DEFAULT_BROWSER_HOME_URL = "https://www.google.com/";
// Server-side screenshot/snapshot commands allow 90s for cold Chromium/canvas captures.
// The client waits slightly longer so valid slow captures surface as captures, not client timeouts.
const BROWSER_OPERATION_TIMEOUT_MS = 100_000;
const INTERACTIVE_SESSION_REFRESH_DELAYS_MS = [1_000, 3_000] as const;

interface BrowserPreviewPanelProps {
  environmentId: EnvironmentId;
  threadId: ThreadId;
  projectId?: ProjectId;
  threadWorktreePath?: string | null;
  initialTabId?: string;
  onClose?: () => void;
}

interface PendingBrowserCommandState {
  readonly token: string | null;
  readonly timeout: number | undefined;
}

interface BrowserCommandErrorContext {
  readonly command: BrowserCommand;
  readonly commandGeneration: number;
  readonly error: unknown;
  readonly options: BrowserCommandRunOptions;
  readonly useNativeBrowserCommand: boolean;
}

export function BrowserPreviewPanel({
  environmentId,
  threadId,
  projectId,
  threadWorktreePath,
  initialTabId,
  onClose,
}: BrowserPreviewPanelProps) {
  const previewSettings = useSettings((state) => ({
    previewRememberLastUrl: state.previewRememberLastUrl,
    previewFollowAgentTab: state.previewFollowAgentTab,
    previewLastUrl: state.previewLastUrl,
    previewInteractionMode: state.previewInteractionMode,
  }));
  const { updateSettings } = useUpdateSettings();
  const threadRef = useMemo(
    () => scopeThreadRef(environmentId, threadId),
    [environmentId, threadId],
  );
  const previewThreadKey = useMemo(() => scopedThreadKey(threadRef), [threadRef]);
  const environmentApi = useEnvironmentApi(environmentId);
  const serverThread = useStore(useMemo(() => createThreadSelectorByRef(threadRef), [threadRef]));
  const projectRef = serverThread
    ? scopeProjectRef(serverThread.environmentId, serverThread.projectId)
    : projectId
      ? scopeProjectRef(environmentId, projectId)
      : null;
  const project = useStore(useMemo(() => createProjectSelectorByRef(projectRef), [projectRef]));
  const browserThreadContext = useMemo(
    () =>
      serverThread
        ? {
            id: serverThread.id,
            worktreePath: serverThread.worktreePath,
            activities: serverThread.activities,
            messages: serverThread.messages,
          }
        : projectId
          ? {
              id: threadId,
              worktreePath: threadWorktreePath ?? null,
              activities: [],
              messages: [],
            }
          : null,
    [projectId, serverThread, threadId, threadWorktreePath],
  );
  const browserThreadState = useBrowserStateStore((state) =>
    selectBrowserThreadState(state.browserStateByThreadKey, threadRef),
  );

  useEffect(() => {
    return () => {
      clearAllTrackedRpcRequests();
    };
  }, []);
  const setSessionSnapshot = useBrowserStateStore((state) => state.setSessionSnapshot);
  const terminalState = useTerminalStateStore((state) =>
    selectThreadTerminalState(state.terminalStateByThreadKey, threadRef),
  );
  const terminalEventEntriesByKey = useTerminalStateStore(
    (state) => state.terminalEventEntriesByKey,
  );
  const terminalSessionSnapshotByKey = useTerminalStateStore(
    (state) => state.terminalSessionSnapshotByKey,
  );
  const terminalPreviewUrlRecord = useTerminalStateStore((state) =>
    selectThreadTerminalPreviewUrl(state.terminalPreviewUrlByThreadKey, threadRef),
  );
  const storeSetTerminalOpen = useTerminalStateStore((state) => state.setTerminalOpen);
  const storeSetActiveTerminal = useTerminalStateStore((state) => state.setActiveTerminal);
  const storeNewTerminal = useTerminalStateStore((state) => state.newTerminal);
  const storeSetTerminalLaunchContext = useTerminalStateStore(
    (state) => state.setTerminalLaunchContext,
  );
  const commentDraft = useBrowserCommentDraftStore((state) =>
    selectBrowserCommentDraft(state.draftByThreadKey, threadRef),
  );
  const setCommentNote = useBrowserCommentDraftStore((state) => state.setNote);
  const setCommentSelecting = useBrowserCommentDraftStore((state) => state.setSelecting);
  const setCommentTarget = useBrowserCommentDraftStore((state) => state.setTarget);
  const clearCommentDraft = useBrowserCommentDraftStore((state) => state.clear);
  const addComposerDraftImage = useComposerDraftStore((state) => state.addImage);
  const [addressValue, setAddressValue] = useState(
    previewSettings.previewRememberLastUrl && previewSettings.previewLastUrl
      ? previewSettings.previewLastUrl
      : DEFAULT_BROWSER_HOME_URL,
  );
  const [dismissedDevHintScriptId, setDismissedDevHintScriptId] = useState<string | null>(null);
  const [launchedDevScriptId, setLaunchedDevScriptId] = useState<string | null>(null);
  const [browserRuntimeError, setBrowserRuntimeError] = useState<string | null>(null);
  const commandPaletteOpen = useCommandPaletteStore((state) => state.open);
  const [hasBrowserRuntime, setHasBrowserRuntime] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [addressInputFocused, setAddressInputFocused] = useState(false);
  const [passiveAttachSettled, setPassiveAttachSettled] = useState(false);
  const [openingBrowserSession, setOpeningBrowserSession] = useState(false);
  const [surfaceViewport, setSurfaceViewport] = useState<BrowserViewport | null>(null);
  const [lifecycleAction, setLifecycleAction] = useState<
    "restart" | "close" | "clear-data" | "console-logs" | null
  >(null);
  const [pendingBrowserCommand, setPendingBrowserCommand] = useState<BrowserCommand["type"] | null>(
    null,
  );
  const [threadDevPort, setThreadDevPort] = useState<number | null>(null);
  // Read by the idle launcher: the dev runtime's own recorded preview URL, which
  // survives a terminal being closed and so outlives the observed-output one.
  const [threadDevPreviewUrl, setThreadDevPreviewUrl] = useState<string | null>(null);
  const [devTunnelRecords, setDevTunnelRecords] = useState<ReadonlyArray<DevTunnelRecord>>([]);
  const devTunnelRecordsRef = useRef<ReadonlyArray<DevTunnelRecord>>([]);
  const addressInputRef = useRef<HTMLInputElement | null>(null);
  const addressInputFocusedRef = useRef(false);
  const activeTabRef = useRef<BrowserTabSnapshot | null>(null);
  const sessionRef = useRef<BrowserSessionSnapshot | null>(null);
  const pendingPointerAddressValueRef = useRef<string | null>(null);
  const pendingBrowserCommandTokenRef = useRef<string | null>(null);
  const commandGenerationRef = useRef(0);
  const browserRuntimeScopeKeyRef = useRef(`${environmentId}:${threadId}`);
  const interactivePointerQueueRef = useRef<Promise<void>>(Promise.resolve());
  const interactiveKeyboardQueueRef = useRef<Promise<void>>(Promise.resolve());
  const lastInteractivePointerPointRef = useRef<{
    readonly tabId: string;
    readonly x: number;
    readonly y: number;
  } | null>(null);
  const historyRestoreAttemptKeyRef = useRef<string | null>(null);
  const autoLaunchKeyRef = useRef<string | null>(null);
  const passiveSessionAttachKeyRef = useRef<string | null>(null);
  const lastActivatedRouteTabIdRef = useRef<string | null>(null);
  const lastSyncedPreviewTabIdRef = useRef<string | null>(null);
  const sessionOpenInitialUrlRef = useRef(
    previewSettings.previewRememberLastUrl && previewSettings.previewLastUrl
      ? previewSettings.previewLastUrl
      : DEFAULT_BROWSER_HOME_URL,
  );
  const readBrowserAddressValue = useCallback(
    (overrideValue?: string | null) =>
      overrideValue ?? addressInputRef.current?.value ?? addressValue,
    [addressValue],
  );
  const setTrackedDevTunnelRecords = useCallback(
    (
      next:
        | ReadonlyArray<DevTunnelRecord>
        | ((records: ReadonlyArray<DevTunnelRecord>) => ReadonlyArray<DevTunnelRecord>),
    ) => {
      const resolved = typeof next === "function" ? next(devTunnelRecordsRef.current) : next;
      devTunnelRecordsRef.current = resolved;
      setDevTunnelRecords(resolved);
    },
    [],
  );

  const {
    activeTab,
    browserDisplay,
    activeConsoleLogs,
    activeConsoleLogsMeta,
    activeFrameIsFresh,
    activeRenderMode,
    activeScreenshot,
    activeScreenshotIsFresh,
    activeScreenshotMeta,
    activeSnapshotText,
    activeTabDisplayUrl,
    activeTabHasVisiblePreview,
    activeTabUrl,
    displayFrame,
    duplicateVisibleTabLabels,
    isAttached: liveDisplayIsAttached,
    recoverableDevPreviewUrl,
    session,
    shouldPreferBrowserCapture,
    visibleSessionTabIdsKey,
    visibleTabLabels,
    visibleTabs,
  } = useMemo(
    () =>
      deriveBrowserPreviewPanelSessionState({
        browserThreadState,
        devTunnelRecords,
        routeTabId: initialTabId,
      }),
    [browserThreadState, devTunnelRecords, initialTabId],
  );
  sessionRef.current = session;
  activeTabRef.current = activeTab;
  const activeConsoleSummary = resolveBrowserConsoleSummary({
    logs: activeConsoleLogs,
    meta: activeConsoleLogsMeta,
  });
  const devServerLaunchPlan = useMemo(
    () => resolveDevServerLaunchPlan(project?.scripts ?? []),
    [project?.scripts],
  );
  const terminalCount = terminalState.terminalIds.length;
  const runningTerminalCount = terminalState.runningTerminalIds.length;
  const latestThreadDevServerPreviewUrl = useMemo(
    () =>
      latestThreadDevServerPreviewUrlFromSources({
        terminalEventEntriesByKey,
        terminalPreviewUrlRecord,
        terminalSessionSnapshotByKey,
        terminalState,
        threadMessages: browserThreadContext?.messages ?? [],
        threadRef,
      }),
    [
      browserThreadContext?.messages,
      terminalEventEntriesByKey,
      terminalPreviewUrlRecord,
      terminalSessionSnapshotByKey,
      terminalState,
      threadRef,
    ],
  );
  const historicalBrowserRestoreUrl = useMemo(
    () => deriveRestorableThreadBrowserUrl(browserThreadContext?.activities ?? []),
    [browserThreadContext?.activities],
  );
  const devServerRelaunchTargetUrl = resolveDevServerRelaunchTargetUrl({
    activeTabDisplayUrl,
    recoverableDevPreviewUrl,
    addressLocalPreviewUrl: null,
    latestThreadDevServerPreviewUrl:
      runningTerminalCount > 0 ? latestThreadDevServerPreviewUrl : null,
  });
  const browserAccessTarget = useMemo(
    () =>
      resolveBrowserAccessTarget({
        activeTab,
        devTunnelRecords,
      }),
    [activeTab, devTunnelRecords],
  );
  const shouldOfferDevServerLaunch = shouldShowDevServerLaunchCard({
    activeTabDisplayUrl: devServerRelaunchTargetUrl,
    hasHealthyActiveBrowserTab: activeTabHasVisiblePreview,
    hasProject: Boolean(project),
    hasThread: Boolean(browserThreadContext),
    devServerLaunchPlanId: devServerLaunchPlan.id,
    devServerLaunchPlanSource: devServerLaunchPlan.source,
    dismissedDevHintScriptId,
    hasVisiblePreview: activeTabHasVisiblePreview,
    launchedDevScriptId,
    runningTerminalCount,
  });
  const devServerLaunchCardVm = resolveBrowserDevServerLaunchCardVm({
    devServerLaunchPlan,
    runningTerminalCount,
    terminalCount,
    latestThreadDevServerPreviewUrl,
    launchedDevScriptId,
  });
  const {
    blockingBrowserError,
    browserAddressBusy,
    browserActionBusy,
    browserBusy,
    browserCommandBusy,
    devServerLaunchBusy,
    previewVm,
    visibleBrowserError,
  } = deriveBrowserPreviewPanelRuntimeState({
    activeScreenshot,
    activeScreenshotMeta,
    activeTab,
    browserRuntimeError,
    browserThreadState,
    connecting,
    displayFrame,
    hasBrowserRuntime,
    lifecycleAction,
    liveDisplayIsAttached,
    openingBrowserSession,
    pendingBrowserCommand,
    session,
  });
  const { interactiveSurfaceViewport, presentation, remoteWebRtcSurfaceViewport } =
    deriveBrowserPreviewPanelInteractionState({
      activeRenderMode,
      browserDisplay,
      canRenderSurface: previewVm.canRenderCanvas,
      client: browserClientCapability(),
      commentSelecting: commentDraft.selecting,
      displayFrame,
      obscuredByHostOverlay: commandPaletteOpen,
      previewInteractionMode: previewSettings.previewInteractionMode,
      session,
      surfaceViewport,
    });

  const showBrowserError = useCallback((error: unknown) => {
    const normalized = normalizeBrowserError(error);
    const message = formatBrowserErrorDescription(error);
    setBrowserRuntimeError(message);
    toastManager.add({
      type: "error",
      title: normalized.title,
      description: message,
    });
  }, []);

  const {
    handleBrowserDisplayUpdate,
    handleNativeBrowserSurfaceError,
    handleNativeBrowserSurfaceResult,
    handleRemoteWebRtcSurfaceError,
  } = useBrowserDisplayLifecycle({
    activeTab,
    activeTabRef,
    addressInputFocusedRef,
    browserDisplay,
    devTunnelRecordsRef,
    environmentId,
    session,
    sessionRef,
    setAddressValue,
    showBrowserError,
    threadId,
    threadRef,
  });
  const lifecycleEffectsOptions = useMemo<BrowserPreviewLifecycleEffectsOptions>(
    () => ({
      activeTab,
      addressInputFocused,
      addressInputFocusedRef,
      browserRuntimeScopeKeyRef,
      commandGenerationRef,
      devTunnelRecords,
      environmentApi,
      environmentId,
      historyRestoreAttemptKeyRef,
      interactiveKeyboardQueueRef,
      interactivePointerQueueRef,
      lastInteractivePointerPointRef,
      lastSyncedPreviewTabIdRef,
      lifecycleAction,
      openingBrowserSession,
      passiveSessionAttachKeyRef,
      pendingBrowserCommand,
      pendingBrowserCommandTokenRef,
      previewLastUrl: previewSettings.previewLastUrl,
      previewRememberLastUrl: previewSettings.previewRememberLastUrl,
      recoverableDevPreviewUrl,
      session,
      sessionOpenInitialUrlRef,
      setAddressInputFocused,
      setAddressValue,
      setBrowserRuntimeError,
      setConnecting,
      setHasBrowserRuntime,
      setOpeningBrowserSession,
      setPassiveAttachSettled,
      setPendingBrowserCommand,
      setSessionSnapshot,
      setThreadDevPort,
      setThreadDevPreviewUrl,
      setTrackedDevTunnelRecords,
      threadId,
      threadRef,
    }),
    [
      activeTab,
      addressInputFocused,
      devTunnelRecords,
      environmentApi,
      environmentId,
      lifecycleAction,
      openingBrowserSession,
      pendingBrowserCommand,
      previewSettings.previewLastUrl,
      previewSettings.previewRememberLastUrl,
      recoverableDevPreviewUrl,
      session,
      setSessionSnapshot,
      setTrackedDevTunnelRecords,
      threadId,
      threadRef,
    ],
  );
  useBrowserPreviewLifecycleEffects(lifecycleEffectsOptions);

  // Create the Playwright session only when the user opens a tab.
  const ensureSession = useBrowserOpenSession({
    browserBusy,
    devTunnelRecordsRef,
    environmentId,
    projectId: project?.cmmdRegistration?.cmmdProjectId ?? null,
    session,
    setBrowserRuntimeError,
    setConnecting,
    setOpeningBrowserSession,
    setSessionSnapshot,
    showBrowserError,
    threadId,
    threadRef,
  });

  const invalidateInFlightBrowserCommands = useCallback(() => {
    commandGenerationRef.current += 1;
    pendingBrowserCommandTokenRef.current = null;
    interactivePointerQueueRef.current = Promise.resolve();
    interactiveKeyboardQueueRef.current = Promise.resolve();
    lastInteractivePointerPointRef.current = null;
    setPendingBrowserCommand(null);
  }, []);

  const releaseBrowserAddressInput = useCallback(() => {
    addressInputFocusedRef.current = false;
    setAddressInputFocused(false);
    addressInputRef.current?.blur();
  }, []);

  const refreshBrowserSessionAfterInteractiveCommand = useCallback(
    (commandGeneration: number) => {
      for (const delayMs of INTERACTIVE_SESSION_REFRESH_DELAYS_MS) {
        window.setTimeout(() => {
          if (commandGenerationRef.current !== commandGeneration) {
            return;
          }
          let api: ReturnType<typeof ensureEnvironmentApi>;
          try {
            api = ensureEnvironmentApi(environmentId);
          } catch {
            return;
          }
          if (!api.browser) {
            return;
          }
          const currentSession = sessionRef.current;
          void api.browser
            .getSession({
              threadId,
              environmentId,
              ...(currentSession?.browserId ? { browserId: currentSession.browserId } : {}),
            })
            .then((refreshed) => {
              if (!refreshed || commandGenerationRef.current !== commandGeneration) {
                return;
              }
              setSessionSnapshot(threadRef, refreshed);
            })
            .catch(() => {
              // Interactive input already succeeded; a best-effort follow-up read
              // should not surface a command failure toast.
              return;
            });
        }, delayMs);
      }
    },
    [environmentId, setSessionSnapshot, threadId, threadRef],
  );

  const showBrowserCommandError = useCallback((title: string, description: string) => {
    setBrowserRuntimeError(description);
    toastManager.add({
      type: "error",
      title,
      description,
    });
  }, []);

  const beginPendingBrowserCommand = useCallback(
    (
      commandType: BrowserCommand["type"] | null,
      timeoutMs: number | undefined,
    ): PendingBrowserCommandState => {
      if (!commandType) {
        return { token: null, timeout: undefined };
      }

      const token = `${commandType}:${randomUUID()}`;
      pendingBrowserCommandTokenRef.current = token;
      setPendingBrowserCommand(commandType);
      const timeout = window.setTimeout(
        () => {
          if (pendingBrowserCommandTokenRef.current !== token) {
            return;
          }
          pendingBrowserCommandTokenRef.current = null;
          setPendingBrowserCommand(null);
        },
        (timeoutMs ?? BROWSER_OPERATION_TIMEOUT_MS) + 250,
      );
      return { token, timeout };
    },
    [],
  );

  const clearPendingBrowserCommand = useCallback((pending: PendingBrowserCommandState) => {
    if (pending.timeout !== undefined) {
      window.clearTimeout(pending.timeout);
    }
    if (pending.token && pendingBrowserCommandTokenRef.current === pending.token) {
      pendingBrowserCommandTokenRef.current = null;
      setPendingBrowserCommand(null);
    }
  }, []);

  const handleBrowserCommandError = useCallback(
    (context: BrowserCommandErrorContext): boolean => {
      if (commandGenerationRef.current !== context.commandGeneration) {
        return false;
      }
      context.options.onError?.(context.error);
      const latestDisplay = sessionRef.current?.display
        ? normalizeBrowserDisplayForPreview(sessionRef.current.display)
        : null;
      const decision = resolveBrowserCommandErrorDecision({
        command: context.command,
        error: context.error,
        latestDisplay,
        useNativeBrowserCommand: context.useNativeBrowserCommand,
      });
      if (decision.type === "recover-display") {
        setBrowserRuntimeError(null);
        handleBrowserDisplayUpdate(decision.display);
        refreshBrowserSessionAfterInteractiveCommand(context.commandGeneration);
        return false;
      }
      if (context.options.reportError !== false) {
        showBrowserError(context.error);
      }
      return false;
    },
    [handleBrowserDisplayUpdate, refreshBrowserSessionAfterInteractiveCommand, showBrowserError],
  );

  const applyServerBrowserCommandResult = useCallback(
    (input: {
      readonly command: BrowserCommand;
      readonly commandGeneration: number;
      readonly result: BrowserCommandRpcResult;
      readonly onSnapshot: ((snapshot: BrowserSessionSnapshot) => void) | undefined;
    }): boolean => {
      if (commandGenerationRef.current !== input.commandGeneration) {
        return false;
      }
      input.onSnapshot?.(input.result.snapshot);
      const decision = resolveServerBrowserCommandResult({
        command: input.command,
        previousSession: sessionRef.current,
        result: input.result,
        devTunnelRecords: devTunnelRecordsRef.current,
      });
      setSessionSnapshot(threadRef, decision.nextSession);
      if (decision.latestCapture) {
        useBrowserStateStore.getState().applyBrowserEvent(threadRef, decision.latestCapture);
      }
      if (decision.latestFrame) {
        useBrowserStateStore.getState().applyBrowserEvent(threadRef, decision.latestFrame);
      }
      if (decision.shouldRefreshSession) {
        refreshBrowserSessionAfterInteractiveCommand(input.commandGeneration);
      }
      return true;
    },
    [refreshBrowserSessionAfterInteractiveCommand, setSessionSnapshot, threadRef],
  );

  const reportUnavailableBrowserCommand = useCallback(
    (
      availability: ReturnType<typeof resolveBrowserCommandRuntimeAvailability>,
      shouldReportError: boolean,
    ): boolean => {
      if (availability.type === "available") {
        return false;
      }
      if (shouldReportError) {
        showBrowserCommandError("Browser command failed", availability.message);
      }
      return true;
    },
    [showBrowserCommandError],
  );

  const runCommand = useBrowserRunCommand({
    activeTabRef,
    beginPendingBrowserCommand,
    clearPendingBrowserCommand,
    commandGenerationRef,
    defaultTimeoutMs: BROWSER_OPERATION_TIMEOUT_MS,
    devTunnelRecordsRef,
    environmentId,
    projectId: project?.cmmdRegistration?.cmmdProjectId ?? null,
    handleBrowserCommandError,
    handleNativeBrowserSurfaceResult,
    onServerCommandResult: applyServerBrowserCommandResult,
    reportUnavailableBrowserCommand,
    session,
    setBrowserRuntimeError,
    threadId,
  });

  const enqueueInteractiveBrowserCommand = useCallback(
    (command: BrowserCommand) => {
      releaseBrowserAddressInput();
      const commandGeneration = commandGenerationRef.current;
      const runIfCurrent = async () => {
        if (commandGenerationRef.current !== commandGeneration) {
          return;
        }
        await runCommand(command, { visiblePending: false });
      };

      const queueRef =
        command.type === "pressKey" || command.type === "typeText"
          ? interactiveKeyboardQueueRef
          : interactivePointerQueueRef;
      const previous = queueRef.current.catch(() => undefined);
      const next = previous.then(runIfCurrent);
      queueRef.current = next.catch(() => undefined);
    },
    [releaseBrowserAddressInput, runCommand],
  );

  const browserOptionsMenuItems = buildBrowserOptionsMenuItems({
    hasSession: Boolean(session),
    canClearData: Boolean(environmentApi?.browser?.clearBrowsingData),
    canReadConsoleLogs: false,
    busy: browserBusy,
  });

  const handleBrowserError = showBrowserError;

  const { handleBrowserOptionsAction, restartBrowserSessionAtUrl } = useBrowserSessionControls({
    activeTab,
    activeTabUrl,
    devTunnelRecordsRef,
    environmentId,
    handleBrowserError,
    invalidateInFlightBrowserCommands,
    lifecycleAction,
    readBrowserAddressValue,
    runCommand,
    session,
    sessionOpenInitialUrlRef,
    setBrowserRuntimeError,
    setLifecycleAction,
    setSessionSnapshot,
    threadId,
    threadRef,
  });

  const runHistoryRestoreDecision = useCallback(
    (decision: BrowserHistoryRestoreDecision) => {
      if (decision.type === "none") {
        return;
      }
      if (historyRestoreAttemptKeyRef.current === decision.attemptKey) {
        return;
      }
      historyRestoreAttemptKeyRef.current = decision.attemptKey;

      if (decision.type === "activate-tab") {
        void runCommand({ type: "activateTab", tabId: decision.tabId });
        return;
      }

      const restore =
        sessionRef.current === null
          ? ensureSession(decision.restoreUrl, { allowWhileConnecting: true })
          : restartBrowserSessionAtUrl(decision.restoreUrl);
      void restore.then((restored) => {
        if (!restored) {
          historyRestoreAttemptKeyRef.current = null;
        }
      });
    },
    [ensureSession, restartBrowserSessionAtUrl, runCommand],
  );

  useEffect(() => {
    if (
      !environmentApi?.browser ||
      !historicalBrowserRestoreUrl ||
      !passiveAttachSettled ||
      connecting ||
      browserActionBusy ||
      addressInputFocused
    ) {
      return;
    }
    const historyRestoreDecision = deriveBrowserHistoryRestoreDecision({
      environmentId,
      threadId,
      restoreUrl: historicalBrowserRestoreUrl,
      session,
      routeTabId: initialTabId,
    });
    runHistoryRestoreDecision(historyRestoreDecision);
  }, [
    initialTabId,
    browserActionBusy,
    connecting,
    addressInputFocused,
    environmentApi,
    environmentId,
    historicalBrowserRestoreUrl,
    passiveAttachSettled,
    runHistoryRestoreDecision,
    session,
    threadId,
  ]);

  const runAddressCommand = useCallback(
    async (command: BrowserAddressCommand): Promise<boolean> => {
      setBrowserRuntimeError(null);
      let commandError: unknown;
      let commandSnapshot: BrowserSessionSnapshot | null = null;
      const ok = await runCommand(command, {
        reportError: false,
        onError: (error) => {
          commandError = error;
        },
        onSnapshot: (snapshot) => {
          commandSnapshot = snapshot;
        },
      });
      if (ok) {
        if (
          !commandSnapshot ||
          browserSessionHasAddressUrl(commandSnapshot, command.url, devTunnelRecordsRef.current)
        ) {
          return true;
        }
        if (isLocalPreviewBrowserUrl(command.url, devTunnelRecordsRef.current)) {
          return restartBrowserSessionAtUrl(command.url);
        }
        return true;
      }
      if (!commandError) {
        return false;
      }
      if (
        isRecoverableBrowserSessionCommandError(commandError) ||
        shouldRestartLocalPreviewAddressCommand({
          command,
          error: commandError,
          devTunnelRecords: devTunnelRecordsRef.current,
        })
      ) {
        return restartBrowserSessionAtUrl(command.url);
      }
      handleBrowserError(commandError);
      return false;
    },
    [handleBrowserError, restartBrowserSessionAtUrl, runCommand],
  );

  useEffect(() => {
    if (!initialTabId) {
      lastActivatedRouteTabIdRef.current = null;
      return;
    }
    if (!session) {
      return;
    }
    const routeSelection = {
      routeTabId: initialTabId,
      knownTabIds: visibleTabs.map((tab) => tab.tabId),
    };
    if (!shouldActivateBrowserTabFromRoute(routeSelection)) {
      return;
    }
    if (lastActivatedRouteTabIdRef.current === routeSelection.routeTabId) {
      return;
    }
    lastActivatedRouteTabIdRef.current = routeSelection.routeTabId;
    if (session.activeTabId === routeSelection.routeTabId) {
      return;
    }
    void runCommand({ type: "activateTab", tabId: routeSelection.routeTabId });
  }, [initialTabId, runCommand, session, visibleSessionTabIdsKey, visibleTabs]);

  useEffect(() => {
    const activeTabId = activeTab?.tabId;
    const tabSync = {
      lastSyncedTabId: lastSyncedPreviewTabIdRef.current,
      routeTabId: initialTabId ?? null,
      activeTabId,
    };
    if (!shouldSyncPreviewTabIdToUrl(tabSync)) {
      return;
    }
    lastSyncedPreviewTabIdRef.current = tabSync.activeTabId;
    // The browser surface *is* the tab, so switching sessions is a surface
    // change rather than a URL write. `openBrowser` reuses the surface for a
    // session already open instead of stacking a duplicate.
    if (previewThreadKey) {
      useRightPanelStore.getState().openBrowser(previewThreadKey, tabSync.activeTabId);
    }
  }, [activeTab?.tabId, initialTabId, previewThreadKey]);

  const {
    handleCanvasTargetChange,
    handleCaptureScreenshotAttachment,
    handleSubmitComment,
    handleToggleAnnotationMode,
    requestScreenshotCapture,
  } = useBrowserCommentCapture({
    activeFrameIsFresh,
    activeRenderMode,
    activeScreenshotIsFresh,
    activeScreenshotMeta,
    activeTab,
    activeTabUrl,
    addComposerDraftImage,
    canAnnotate: previewVm.canAnnotate,
    clearCommentDraft,
    commentOrchestration: environmentApi?.orchestration,
    commentDraft,
    hasServerThread: Boolean(serverThread),
    runCommand,
    serverThread,
    session,
    setCommentSelecting,
    setCommentTarget,
    shouldPreferBrowserCapture,
    threadId,
    threadRef,
  });

  const handleContainerResize = useBrowserViewportSync({
    hasBrowserSession: Boolean(session?.browserId),
    runCommand,
    sessionViewport: session?.viewport ?? null,
    setSurfaceViewport,
  });

  const { ensureDevTunnelPreviewUrl, submitBrowserAddress } = useBrowserAddressCommands({
    activeTab,
    blockingBrowserError,
    browserActionBusy,
    browserAddressBusy,
    devTunnelRecordsRef,
    ensureSession,
    environmentId,
    liveDisplayIsAttached,
    project,
    previewRememberLastUrl: previewSettings.previewRememberLastUrl,
    readBrowserAddressValue,
    restartBrowserSessionAtUrl,
    runAddressCommand,
    session,
    setAddressValue,
    setThreadDevPreviewUrl,
    setTrackedDevTunnelRecords,
    threadId,
    updateSettings,
  });

  const onNavigate = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      await submitBrowserAddress("navigate-active-tab");
    },
    [submitBrowserAddress],
  );

  const viewportKeyForAnnotationCleanup = `${session?.viewport.width}x${session?.viewport.height}`;
  const prevViewportKeyRef = useRef(viewportKeyForAnnotationCleanup);
  useEffect(() => {
    if (prevViewportKeyRef.current !== viewportKeyForAnnotationCleanup) {
      prevViewportKeyRef.current = viewportKeyForAnnotationCleanup;
      clearCommentDraft(threadRef);
    }
  }, [clearCommentDraft, threadRef, viewportKeyForAnnotationCleanup]);

  const handleOpenExternal = useCallback((url: string) => {
    const localApi = readLocalApi();
    if (!localApi) {
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }
    void localApi.shell.openExternal(url).catch((error) => {
      toastManager.add({
        type: "error",
        title: "Could not open browser",
        description: error instanceof Error ? error.message : "Failed to open external browser.",
      });
    });
  }, []);

  const handleLaunchDevServer = useBrowserDevServerLauncher({
    blockingBrowserError,
    browserThreadContext,
    devServerLaunchBusy,
    devServerLaunchPlan,
    devTunnelRecordsRef,
    environmentApi,
    ensureDevTunnelPreviewUrl,
    ensureSession,
    liveDisplayIsAttached,
    latestThreadDevServerPreviewUrl,
    previewRememberLastUrl: previewSettings.previewRememberLastUrl,
    project,
    restartBrowserSessionAtUrl,
    runAddressCommand,
    runningTerminalCount,
    session,
    setAddressValue,
    setConnecting,
    setLaunchedDevScriptId,
    setThreadDevPort,
    setThreadDevPreviewUrl,
    storeNewTerminal,
    storeSetActiveTerminal,
    storeSetTerminalLaunchContext,
    storeSetTerminalOpen,
    threadDevPort,
    threadId,
    threadRef,
    updateSettings,
  });

  useEffect(() => {
    if (!recoverableDevPreviewUrl || devServerLaunchPlan.source !== "project-script") {
      return;
    }
    if (!shouldOfferDevServerLaunch || runningTerminalCount > 0) {
      return;
    }
    if (launchedDevScriptId === devServerLaunchPlan.id) {
      return;
    }
    if (!browserThreadContext) {
      return;
    }

    const launchKey = `${browserThreadContext.id}:${recoverableDevPreviewUrl}:${devServerLaunchPlan.id}`;
    if (autoLaunchKeyRef.current === launchKey) {
      return;
    }
    autoLaunchKeyRef.current = launchKey;
    void handleLaunchDevServer();
  }, [
    devServerLaunchPlan.id,
    devServerLaunchPlan.source,
    handleLaunchDevServer,
    launchedDevScriptId,
    recoverableDevPreviewUrl,
    runningTerminalCount,
    shouldOfferDevServerLaunch,
    browserThreadContext,
  ]);

  // History stores the display URL, never the signed tunnel URL the tab is
  // actually on: a `forgeTunnelToken` in localStorage would outlive the tunnel
  // that minted it, and would be printed straight back into the launcher.
  useRecordBrowserVisit({
    environmentId,
    projectId: project?.id ?? null,
    threadId,
    url: browserDisplayUrl(activeTab?.url ?? null, devTunnelRecords),
    title: activeTab?.title ?? null,
    navigationStatus: activeTab?.navigationStatus ?? null,
  });

  const launcherVisible = shouldShowBrowserLauncher(previewVm.status);
  const launcher = useBrowserLauncher({
    environmentId,
    projectId: project?.id ?? null,
    threadId,
    // Only claim the observed URL is live while a terminal is actually running,
    // the same condition the launch card uses for "Open app".
    devServerPreviewUrl: runningTerminalCount > 0 ? latestThreadDevServerPreviewUrl : null,
    devRuntimePreviewUrl: browserDisplayUrl(threadDevPreviewUrl, devTunnelRecords),
    onOpenUrl: (url) => {
      setAddressValue(url);
      void submitBrowserAddress("navigate-active-tab", url);
    },
    launchAction: shouldOfferDevServerLaunch
      ? {
          label: devServerLaunchCardVm.title,
          detail:
            devServerLaunchPlan.source === "project-script"
              ? `${devServerLaunchPlan.name}: ${devServerLaunchPlan.command}`
              : devServerLaunchCardVm.description,
          actionLabel: devServerLaunchCardVm.actionLabel,
          busy: devServerLaunchBusy,
          onLaunch: () => void handleLaunchDevServer(),
        }
      : undefined,
  });

  return (
    <div className="flex h-full min-h-0 flex-col bg-card">
      <div className="border-border border-b bg-card">
        <BrowserChrome
          activeConsoleLogsMeta={activeConsoleLogsMeta}
          activeConsoleSummary={activeConsoleSummary}
          activeTab={activeTab}
          addressInputFocusedRef={addressInputFocusedRef}
          addressInputRef={addressInputRef}
          addressValue={addressValue}
          browserAccessTarget={browserAccessTarget}
          browserAddressBusy={browserAddressBusy}
          browserCommandBusy={browserCommandBusy}
          browserOptionsMenuItems={browserOptionsMenuItems}
          commentDraft={commentDraft}
          devTunnelRecords={devTunnelRecords}
          duplicateVisibleTabLabels={duplicateVisibleTabLabels}
          lifecycleAction={lifecycleAction}
          onCaptureScreenshot={handleCaptureScreenshotAttachment}
          onClose={onClose}
          onNavigate={onNavigate}
          onOpenExternal={handleOpenExternal}
          onOptionsAction={handleBrowserOptionsAction}
          onRunCommand={runCommand}
          onSetAddressInputFocused={setAddressInputFocused}
          onSetAddressValue={setAddressValue}
          onSubmitBrowserAddress={submitBrowserAddress}
          onToggleAnnotationMode={handleToggleAnnotationMode}
          pendingBrowserCommand={pendingBrowserCommand}
          pendingPointerAddressValueRef={pendingPointerAddressValueRef}
          previewVm={previewVm}
          readBrowserAddressValue={readBrowserAddressValue}
          session={session}
          visibleTabLabels={visibleTabLabels}
          visibleTabs={visibleTabs}
        />
      </div>
      {/*
       * The strip is a hint over live content. When the panel is idle the
       * launcher below presents the same action with room to read, so showing
       * both would offer "Start app" twice in one pane.
       */}
      {shouldOfferDevServerLaunch && !launcherVisible ? (
        <BrowserDevServerLaunchCard
          busy={devServerLaunchBusy}
          cardVm={devServerLaunchCardVm}
          launchPlan={devServerLaunchPlan}
          onDismiss={() => setDismissedDevHintScriptId(devServerLaunchPlan.id)}
          onLaunch={() => void handleLaunchDevServer()}
          onShowTerminals={() => storeSetTerminalOpen(threadRef, true)}
        />
      ) : null}
      <div className="min-h-0 flex-1 overflow-hidden">
        <BrowserSurfaceHost
          activeRenderMode={activeRenderMode}
          activeScreenshot={activeScreenshot}
          activeScreenshotMeta={activeScreenshotMeta}
          activeTab={activeTab}
          activeTabUrl={activeTabUrl}
          browserDisplay={browserDisplay}
          browserThreadState={browserThreadState}
          commentDraft={commentDraft}
          displayFrame={displayFrame}
          enqueueInteractiveBrowserCommand={enqueueInteractiveBrowserCommand}
          environmentId={environmentId}
          handleBrowserDisplayUpdate={handleBrowserDisplayUpdate}
          handleCanvasTargetChange={handleCanvasTargetChange}
          handleContainerResize={handleContainerResize}
          handleNativeBrowserSurfaceError={handleNativeBrowserSurfaceError}
          handleNativeBrowserSurfaceResult={handleNativeBrowserSurfaceResult}
          handleOpenExternal={handleOpenExternal}
          handleRemoteWebRtcSurfaceError={handleRemoteWebRtcSurfaceError}
          handleSubmitComment={handleSubmitComment}
          interactiveSurfaceViewport={interactiveSurfaceViewport}
          lastInteractivePointerPointRef={lastInteractivePointerPointRef}
          launcher={launcher}
          presentation={presentation}
          previewVm={previewVm}
          remoteWebRtcSurfaceViewport={remoteWebRtcSurfaceViewport}
          requestScreenshotCapture={requestScreenshotCapture}
          session={session}
          shouldPreferBrowserCapture={shouldPreferBrowserCapture}
          setCommentNote={setCommentNote}
          clearCommentDraft={clearCommentDraft}
          threadId={threadId}
          threadRef={threadRef}
        />
      </div>
      {activeSnapshotText ? (
        <div className="max-h-32 overflow-auto border-border border-t bg-background p-2 font-mono text-[11px] text-muted-foreground">
          {activeSnapshotText}
        </div>
      ) : null}
      {visibleBrowserError ? (
        <div className="border-border border-t bg-destructive-surface px-2 py-1 text-destructive text-xs">
          <div className="flex items-center gap-1.5">
            <ActivityIcon className="size-3.5" />
            <span>{visibleBrowserError}</span>
          </div>
        </div>
      ) : null}
    </div>
  );
}
